import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassInput } from '@/components/ui/GlassInput'
import { GlassButton } from '@/components/ui/GlassButton'
import { addPasskey, signInAnonymously, useSession } from '@/lib/auth-client'
import { api, setActiveWorkspaceId } from '@/lib/api'
import { Fingerprint } from 'lucide-react'
import config from '@config'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/auth/register')({
  component: RegisterPage,
})

function RegisterPage() {
  const t = useT()
  const navigate = useNavigate()
  const { data: session } = useSession()
  const [email, setEmail] = useState('')
  const [newsletterOptIn, setNewsletterOptIn] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  function defaultWorkspaceName(normalizedEmail: string) {
    const localPart = normalizedEmail.split('@')[0]?.trim()
    if (localPart) return `${localPart}'s Workspace`
    return 'My Workspace'
  }

  async function ensureWorkspace(name: string) {
    const workspaceData = await api.workspaces.list()
    if (workspaceData.activeWorkspaceId) {
      setActiveWorkspaceId(workspaceData.activeWorkspaceId)
      return
    }

    if (workspaceData.workspaces.length > 0) {
      const firstWorkspaceId = workspaceData.workspaces[0]?.id ?? null
      if (firstWorkspaceId) setActiveWorkspaceId(firstWorkspaceId)
      return
    }

    const createdWorkspace = await api.workspaces.create({ name })
    if (createdWorkspace?.id) {
      setActiveWorkspaceId(createdWorkspace.id)
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const normalizedEmail = email.trim().toLowerCase()

      const user = session?.user as { isAnonymous?: boolean } | undefined
      const hasAnonymousSession = user?.isAnonymous === true

      if (!hasAnonymousSession) {
        const signInRes = await signInAnonymously()
        const signInErrorCode = (signInRes?.error as { code?: string } | null)?.code
        if (signInRes?.error && signInErrorCode !== 'ANONYMOUS_USERS_CANNOT_SIGN_IN_AGAIN_ANONYMOUSLY') {
          throw new Error(signInRes.error.message ?? t('auth.register.registrationFailed'))
        }
      }

      const passkeyRes = await addPasskey(normalizedEmail ? { name: normalizedEmail } : undefined)
      if (passkeyRes?.error) throw new Error(passkeyRes.error.message ?? t('auth.register.passkeyRequired'))

      if (normalizedEmail) {
        await api.onboarding.contact.save({
          email: normalizedEmail,
          newsletterOptIn,
        })
      }

      await ensureWorkspace(defaultWorkspaceName(normalizedEmail))
      await navigate({ to: '/dashboard' })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.register.registrationFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <GlassCard glow className="w-full max-w-md space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-2xl font-bold text-white glow-text">{config.app.name}</h1>
          <p className="text-sm text-white/50">{t('auth.register.title')}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <GlassInput
            label={t('auth.register.emailOptional')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.register.emailPlaceholder')}
            autoComplete="email"
            hint={t('auth.register.emailOptionalHint')}
          />

          {email.trim().length > 0 && (
            <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
              <input
                type="checkbox"
                checked={newsletterOptIn}
                onChange={(e) => setNewsletterOptIn(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-white/30 bg-transparent text-[var(--oc-accent-light)]"
              />
              <span className="text-sm text-white/70">{t('auth.register.newsletterOptIn')}</span>
            </label>
          )}

          {error && <p className="text-red-400 text-sm">{error}</p>}

          <GlassButton type="submit" size="lg" className="w-full" loading={loading}>
            <Fingerprint size={16} />
            {t('auth.register.createWithPasskey')}
          </GlassButton>
        </form>

        <p className="text-center text-sm text-white/40">
          {t('auth.register.haveAccount')}{' '}
          <Link to="/auth/login" className="text-[var(--oc-accent-light)] hover:underline">
            {t('auth.register.signIn')}
          </Link>
        </p>
      </GlassCard>
    </AuthLayout>
  )
}

function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--oc-bg)] p-4">
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 60% at 50% 50%, rgba(124,58,237,0.08) 0%, transparent 70%)',
        }}
        aria-hidden="true"
      />
      {children}
    </div>
  )
}
